﻿(() => {
  const rootSelector = '[data-cpdp-root]';
  const bodyClass = 'cpdp-page';
  const modalClass = 'cpdp-modal-open';
  const cache = new Map();

  const parseJson = (node) => {
    if (!node) return null;
    try {
      return JSON.parse(node.textContent.trim());
    } catch {
      return null;
    }
  };

  const isDesktop = () => window.innerWidth >= 1200;

  const probeImage = (url) => {
    if (!url) return Promise.resolve(null);
    if (cache.has(url)) return cache.get(url);
    const promise = new Promise((resolve) => {
      const image = new Image();
      image.onload = () => resolve(url);
      image.onerror = () => resolve(null);
      image.src = url;
    });
    cache.set(url, promise);
    return promise;
  };

  const firstValidUrl = async (urls) => {
    for (const url of urls.filter(Boolean)) {
      const valid = await probeImage(url);
      if (valid) return valid;
    }
    return null;
  };

  const currentBreakpoint = () => {
    const width = window.innerWidth;
    if (width <= 767) return 'mobile';
    if (width <= 1023) return 'tablet';
    return 'desktop';
  };

  const getFocusable = (container) => Array.from(container.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')).filter((node) => !node.hidden);

  const hideFloatingWidgets = () => {
    if (!document.body.classList.contains(bodyClass)) return;
    const nodes = Array.from(document.body.querySelectorAll('*'));
    nodes.forEach((node) => {
      const text = (node.textContent || '').trim().toUpperCase();
      if (!text || !text.includes('PICK FOR ROOM')) return;
      const style = window.getComputedStyle(node);
      if (style.position !== 'fixed') return;
      node.style.setProperty('display', 'none', 'important');
      node.style.setProperty('visibility', 'hidden', 'important');
      node.style.setProperty('pointer-events', 'none', 'important');
      node.setAttribute('aria-hidden', 'true');
    });
  };

  const cleanupQuestionMarks = (root) => {
    root.querySelectorAll('button, span, div').forEach((node) => {
      if (node.querySelector('svg')) return;
      const text = (node.textContent || '').trim();
      if (text === '?' || text === '�') {
        if (node.tagName === 'BUTTON') {
          node.textContent = '';
        } else {
          node.remove();
        }
      }
    });
  };

  const normalizeTitleParts = (rawTitle, fallbackType) => {
    const normalizedQuotes = (rawTitle || '')
      .replace(/[\u2018\u2019\u201A\u201B]/g, "'")
      .replace(/[\u201C\u201D\u201E]/g, '"');
    let cleaned = normalizedQuotes.replace(/^[\s'"]+|[\s'"]+$/g, '').toUpperCase();
    const pieceRegex = /(SET OF\s*[234]|[234]\s*PIECES?|[234]\s*PIECE)/i;
    const categoryRegex = /(GLASS WALL ART|CANVAS WALL ART)/i;
    const separatorRegex = /\s*\|\|?\s*/;
    const pieceMatch = cleaned.match(pieceRegex);
    const categoryMatch = cleaned.match(categoryRegex);
    const piece = pieceMatch ? pieceMatch[0].replace(/\s{2,}/g, ' ').trim() : '';
    let category = '';
    if (categoryMatch) {
      category = categoryMatch[0].replace(/\s{2,}/g, ' ').trim();
    } else {
      const ft = (fallbackType || '').toUpperCase();
      if (ft.includes('GLASS')) category = 'GLASS WALL ART';
      else if (ft.includes('CANVAS')) category = 'CANVAS WALL ART';
      else category = 'GLASS WALL ART';
    }
    let main = cleaned
      .replace(categoryRegex, '')
      .replace(pieceRegex, '')
      .replace(/\|\|/g, ' ')
      .replace(/\|/g, ' ')
      .replace(/>/g, ' ')
      .replace(/\s*(DESIGNER\s+COLLECTION|COLLECTION|DESIGNER\s+EDITION|EDITION|PREMIUM)\s*/gi, ' ')
      .replace(/\s{2,}/g, ' ')
      .trim();
    if (separatorRegex.test(main)) {
      main = main.split(separatorRegex)[0].trim();
    }
    main = main.replace(/^[\s'"]+|[\s'"]+$/g, '').replace(/\s{2,}/g, ' ').trim();
    if (!main || main.length < 2) {
      main = cleaned.replace(categoryRegex, '').replace(/[|>]/g, ' ').replace(/\s{2,}/g, ' ').trim();
    }
    return { piece, main, category };
  };

  const titleSizeForDesktop = (length) => {
    if (length <= 8) return 72;
    if (length <= 12) return 62;
    if (length <= 16) return 52;
    if (length <= 20) return 44;
    if (length <= 28) return 38;
    return 32;
  };

  const applyTitleLayout = (root) => {
    const rawTitle = root.dataset.productTitle || '';
    const productType = root.dataset.productType || '';
    const titleNode = root.querySelector('[data-main-title]');
    const categoryNode = root.querySelector('[data-category-line]');
    if (!titleNode || !categoryNode) return;
    const parts = normalizeTitleParts(rawTitle, productType);
    const title = (parts.main || '').trim() || rawTitle.trim();
    titleNode.textContent = title;
    titleNode.setAttribute('title', title);
    titleNode.classList.toggle('cpdp-panel__title--compact', title.length > 20);
    titleNode.classList.toggle('cpdp-panel__title--micro', title.length > 28);
    categoryNode.textContent = parts.category;
    categoryNode.classList.toggle('is-hidden', !parts.category);
    root.style.setProperty('--desktop-title-size', `${titleSizeForDesktop(title.length)}px`);
  };

  const applyRightPanelDesktopState = (root) => {
    const panel = root.querySelector('#cinematic-right-panel');
    if (!panel) return;
    panel.querySelectorAll('.cinematic-action-chips__item').forEach((item) => {
      if ((item.textContent || '').trim().toUpperCase() === 'SEE IT ON YOUR WALL') {
        item.remove();
      }
    });
    panel.querySelectorAll('[data-option-group]').forEach((group) => {
      const cards = Array.from(group.querySelectorAll('.cpdp-options__card'));
      if (cards.length === 5) {
        group.classList.add('cpdp-options--five');
        cards[cards.length - 1].classList.add('cpdp-options__card--full');
      }
    });
  };

  const initRail = (root) => {
    const rail = root.querySelector('#cinematic-left-rail');
    if (!rail) return;
    let track = rail.querySelector('.cinematic-left-rail-track');
    if (!track) {
      track = document.createElement('div');
      track.className = 'cinematic-left-rail-track';
      track.setAttribute('data-rail-track', '');
      const thumbs = Array.from(rail.querySelectorAll('.cpdp__thumb'));
      thumbs.forEach((thumb) => track.appendChild(thumb));
      rail.appendChild(track);
    }
    let up = root.querySelector('[data-rail-up]');
    let down = root.querySelector('[data-rail-down]');
    if (!up) {
      up = document.createElement('button');
      up.type = 'button';
      up.className = 'cpdp__thumb-rail-nav cpdp__thumb-rail-nav--up';
      up.setAttribute('data-rail-up', '');
      up.setAttribute('aria-label', 'Scroll thumbnails up');
      up.innerHTML = '<svg viewBox="0 0 14 14" role="presentation" focusable="false"><path d="M3.2 8.8 7 5l3.8 3.8" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"></path></svg>';
      rail.parentElement.appendChild(up);
    }
    if (!down) {
      down = document.createElement('button');
      down.type = 'button';
      down.className = 'cpdp__thumb-rail-nav cpdp__thumb-rail-nav--down';
      down.setAttribute('data-rail-down', '');
      down.setAttribute('aria-label', 'Scroll thumbnails down');
      down.innerHTML = '<svg viewBox="0 0 14 14" role="presentation" focusable="false"><path d="M3.2 5.2 7 9l3.8-3.8" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"></path></svg>';
      rail.parentElement.appendChild(down);
    }
    const thumbs = Array.from(track.querySelectorAll('.cpdp__thumb'));
    let offset = 0;
    const gap = 14;
    const step = () => {
      const first = thumbs[0];
      return (first ? first.getBoundingClientRect().height : 106) + gap;
    };
    const apply = () => {
      const maxOffset = Math.max(0, track.scrollHeight - rail.clientHeight);
      offset = Math.max(0, Math.min(offset, maxOffset));
      rail.style.setProperty('--rail-offset', `${-offset}px`);
      up.hidden = offset <= 0;
      down.hidden = maxOffset <= 0 || offset >= maxOffset - 1;
    };
    up.addEventListener('click', () => {
      offset -= step();
      apply();
    });
    down.addEventListener('click', () => {
      offset += step();
      apply();
    });
    window.addEventListener('resize', apply);
    apply();
  };

  const initModal = (root) => {
    const modal = root.querySelector('[data-gallery-modal]');
    if (!modal) return;
    const backdrop = modal.querySelector('.cinematic-modal__backdrop');
    const frame = modal.querySelector('.cinematic-modal__frame');
    const closeButton = modal.querySelector('.cinematic-modal__close');
    const prev = modal.querySelector('[data-gallery-prev]');
    const next = modal.querySelector('[data-gallery-next]');
    const counter = modal.querySelector('[data-gallery-counter]');
    const stage = modal.querySelector('[data-gallery-stage]');
    const pageTriggers = Array.from(root.querySelectorAll('[data-gallery-open]'));
    const firstTrigger = root.querySelector('[data-gallery-open-first]');
    let slides = Array.from(modal.querySelectorAll('[data-gallery-slide]'));
    let modalThumbs = [];
    let activeId = slides[0]?.dataset.gallerySlide || null;
    let lastTrigger = null;
    let touchStart = 0;

    if (!frame.querySelector('.cinematic-modal__rail')) {
      const rail = document.createElement('div');
      rail.className = 'cinematic-modal__rail';
      const mediaFrame = document.createElement('div');
      mediaFrame.className = 'cinematic-modal__media-frame';
      slides.forEach((slide, index) => {
        mediaFrame.appendChild(slide);
        const trigger = pageTriggers[index];
        const thumb = document.createElement('button');
        thumb.type = 'button';
        thumb.className = `cinematic-modal__thumb${index === 0 ? ' is-active' : ''}`;
        thumb.setAttribute('data-gallery-thumb', '');
        thumb.dataset.mediaId = slide.dataset.gallerySlide;
        thumb.setAttribute('aria-label', `Open gallery media ${index + 1}`);
        const sourceImage = trigger?.querySelector('img');
        const img = document.createElement('img');
        img.src = sourceImage?.currentSrc || sourceImage?.src || '';
        img.alt = sourceImage?.alt || root.dataset.productTitle || '';
        img.loading = 'lazy';
        img.width = 72;
        img.height = 90;
        thumb.appendChild(img);
        if (slide.querySelector('video, iframe')) {
          const badge = document.createElement('span');
          badge.className = 'cpdp__thumb-play';
          badge.setAttribute('aria-hidden', 'true');
          badge.innerHTML = '<svg viewBox="0 0 10 10" role="presentation" focusable="false"><path d="M3 2.2 7.2 5 3 7.8V2.2Z" fill="currentColor"></path></svg>';
          thumb.appendChild(badge);
        }
        rail.appendChild(thumb);
      });
      stage.textContent = '';
      stage.appendChild(mediaFrame);
      if (prev) stage.appendChild(prev);
      if (next) stage.appendChild(next);
      frame.appendChild(rail);
      frame.appendChild(stage);
    }

    slides = Array.from(modal.querySelectorAll('[data-gallery-slide]'));
    modalThumbs = Array.from(modal.querySelectorAll('[data-gallery-thumb]'));

    if (closeButton) {
      closeButton.innerHTML = '<svg viewBox="0 0 16 16" role="presentation" focusable="false"><path d="M4 4 12 12M12 4 4 12" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.6"></path></svg>';
    }
    if (prev) {
      prev.innerHTML = '<svg viewBox="0 0 16 16" role="presentation" focusable="false"><path d="M9.8 3.5 5.2 8l4.6 4.5" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.6"></path></svg>';
    }
    if (next) {
      next.innerHTML = '<svg viewBox="0 0 16 16" role="presentation" focusable="false"><path d="M6.2 3.5 10.8 8l-4.6 4.5" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.6"></path></svg>';
    }

    const lazyLoadSlide = (slide) => {
      slide.querySelectorAll('img[data-src]').forEach((img) => {
        if (img.dataset.src) {
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
        }
        if (img.dataset.srcset) {
          img.srcset = img.dataset.srcset;
          img.removeAttribute('data-srcset');
        }
      });
    };

    const setActive = (id) => {
      activeId = String(id || activeId);
      const activeIndex = Math.max(slides.findIndex((s) => String(s.dataset.gallerySlide) === activeId), 0);
      slides.forEach((slide, i) => {
        const diff = i - activeIndex;
        slide.hidden = false;
        slide.classList.remove('is-active', 'is-prev', 'is-next', 'is-far');
        if (diff === 0) {
          slide.classList.add('is-active');
          slide.dataset.pos = 'active';
          lazyLoadSlide(slide);
        } else if (diff === -1 || (activeIndex === 0 && i === slides.length - 1)) {
          slide.classList.add('is-prev');
          slide.dataset.pos = 'prev';
          lazyLoadSlide(slide);
        } else if (diff === 1 || (activeIndex === slides.length - 1 && i === 0)) {
          slide.classList.add('is-next');
          slide.dataset.pos = 'next';
          lazyLoadSlide(slide);
        } else {
          slide.classList.add('is-far');
          slide.dataset.pos = 'far';
        }
        if (diff !== 0) slide.querySelectorAll('video').forEach((v) => v.pause());
      });
      if (counter) counter.textContent = (activeIndex + 1) + ' / ' + slides.length;
      pageTriggers.forEach((trigger) => trigger.classList.toggle('is-active', String(trigger.dataset.mediaId) === activeId));
      modalThumbs.forEach((thumb) => thumb.classList.toggle('is-active', String(thumb.dataset.mediaId) === activeId));
    };

    const open = (id, trigger) => {
      lastTrigger = trigger || document.activeElement;
      setActive(id || activeId || slides[0]?.dataset.gallerySlide);
      slides.forEach((slide) => {
        if (!slide.hidden) lazyLoadSlide(slide);
      });
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add(modalClass);
      closeButton?.focus();
    };

    const close = () => {
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove(modalClass);
      if (lastTrigger) lastTrigger.focus();
    };

    const step = (direction) => {
      const index = Math.max(slides.findIndex((slide) => String(slide.dataset.gallerySlide) === activeId), 0);
      const nextIndex = (index + direction + slides.length) % slides.length;
      setActive(slides[nextIndex]?.dataset.gallerySlide);
    };

    pageTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        /* All breakpoints: open gallery modal */
        open(trigger.dataset.mediaId, trigger);
      });
      if (trigger.querySelector('video, iframe')) {
        return;
      }
    });
    modalThumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => setActive(thumb.dataset.mediaId));
    });
    firstTrigger?.addEventListener('click', () => open(slides[0]?.dataset.gallerySlide, firstTrigger));
    backdrop?.addEventListener('click', close);
    closeButton?.addEventListener('click', close);
    /* Close when clicking empty area of the frame (outside image/rail/arrows) */
    frame?.addEventListener('click', (e) => {
      if (e.target === frame || e.target === stage || e.target.classList.contains('cinematic-modal__media-frame')) {
        close();
      }
    });
    prev?.addEventListener('click', () => step(-1));
    next?.addEventListener('click', () => step(1));

    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        step(-1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        step(1);
      }
      if (event.key === 'Tab') {
        const focusable = getFocusable(modal);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!first || !last) return;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
        if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });

    stage?.addEventListener('touchstart', (event) => {
      touchStart = event.changedTouches[0].clientX;
    }, { passive: true });

    stage?.addEventListener('touchend', (event) => {
      const delta = event.changedTouches[0].clientX - touchStart;
      if (Math.abs(delta) < 40) return;
      step(delta > 0 ? -1 : 1);
    }, { passive: true });

    setActive(activeId);
  };

  const initHero = (root, variants) => {
    const config = parseJson(root.querySelector('[data-hero-config]')) || {};
    const desktopImage = root.querySelector('[data-hero-desktop]');
    const tabletSource = root.querySelector('[data-hero-tablet]');
    const mobileSource = root.querySelector('[data-hero-mobile]');

    const update = async (variant) => {
      const breakpoint = currentBreakpoint();
      /* Fast path: use variant metafield override or section-settings hero directly */
      const directUrl = variant?.hero_override?.[breakpoint] || config.manual?.[breakpoint];
      if (directUrl) {
        if (breakpoint === 'mobile' && mobileSource) mobileSource.srcset = directUrl;
        if (breakpoint === 'tablet' && tabletSource) tabletSource.srcset = directUrl;
        if (desktopImage) desktopImage.src = directUrl;
        return;
      }
      const candidates = [];
      if (variant?.hero_candidates?.[breakpoint]) candidates.push(...variant.hero_candidates[breakpoint]);
      if (config.manual?.[breakpoint]) candidates.push(config.manual[breakpoint]);
      if (variant?.featured?.[breakpoint]) candidates.push(variant.featured[breakpoint]);
      if (config.fallback?.[breakpoint]) candidates.push(config.fallback[breakpoint]);
      const resolved = (await firstValidUrl(candidates)) || config.manual?.[breakpoint] || variant?.featured?.[breakpoint] || config.fallback?.[breakpoint] || null;
      if (!resolved) return;
      if (breakpoint === 'mobile' && mobileSource) mobileSource.srcset = resolved;
      if (breakpoint === 'tablet' && tabletSource) tabletSource.srcset = resolved;
      if (desktopImage) desktopImage.src = breakpoint === 'desktop' ? (config.manual?.desktop || resolved) : resolved;
    };

    window.addEventListener('resize', () => update(root.__cpdpVariant || variants[0] || null));
    update(variants[0] || null);

    return {
      updateForVariant(variant) {
        root.__cpdpVariant = variant;
        update(variant);
      }
    };
  };

  const injectShopPayMark = (root) => {
    const mark = root.querySelector('[data-shop-pay-mark]');
    if (!mark) return;
    if (mark.querySelector('img')) return;
    const img = document.createElement('img');
    img.src = (window.Shopify?.routes?.root || '/') + 'cdn/shop/t/1/assets/shop-pay-logo.png';
    const assetUrl = document.querySelector('link[href*="cinematic-product.css"]');
    if (assetUrl) {
      const base = assetUrl.href.replace(/cinematic-product\.css.*$/, '');
      img.src = base + 'shop-pay-logo.png';
    }
    img.alt = 'Shop Pay';
    img.width = 64;
    img.height = 16;
    img.style.cssText = 'display:inline-block;vertical-align:-2px;height:16px;width:auto';
    img.loading = 'lazy';
    mark.appendChild(img);
  };

  const bindWallPreview = (root) => {
    const launcher = root.querySelector('#cinematic-room-launcher');
    if (!launcher) return;
    const button = launcher.querySelector('[data-chip-launch="wall-preview"]');
    if (button) {
      launcher.addEventListener('click', () => {
        button.click();
      });
      launcher.style.cursor = 'pointer';
    }
  };

  const initForm = (root, heroApi) => {
    const form = root.querySelector('[data-type="add-to-cart-form"]');
    if (!form) return;
    const variants = parseJson(form.querySelector('[data-variant-json]')) || [];
    const variantId = form.querySelector('[data-variant-id]');
    const price = form.querySelector('[data-price]');
    const compare = form.querySelector('[data-compare-price]');
    const discount = form.querySelector('[data-discount-pill]');
    const installment = form.querySelector('[data-installment]');
    const selectedLabel = form.querySelector('[data-option-selected]');
    const optionInputs = Array.from(form.querySelectorAll('[data-option-input]'));
    const addButton = form.querySelector('[data-add-to-cart]');
    const mobileAdd = form.querySelector('[data-add-to-cart-mobile]');
    const mobileBar = form.querySelector('[data-mobile-bar]');
    const mobilePrice = form.querySelector('[data-mobile-price]');
    const primaryLabel = addButton ? addButton.textContent.split('—')[0].trim() : 'ADD TO CART';

    const getSelectedValue = () => optionInputs.find((input) => input.checked)?.value;

    const findVariant = () => {
      if (!optionInputs.length) return variants[0] || null;
      const selected = getSelectedValue();
      return variants.find((variant) => variant.title === selected || variant.options?.[0] === selected) || variants[0] || null;
    };

    const update = (variant) => {
      if (!variant) return;
      if (variantId) variantId.value = variant.id;
      if (price) {
        const hasMultipleVariants = variants.length > 1;
        price.textContent = variant.price_text;
      }
      if (compare) {
        const showCompare = Number(variant.compare_at_price || 0) > Number(variant.price || 0);
        compare.textContent = showCompare ? variant.compare_at_price_text : '';
        compare.classList.toggle('is-hidden', !showCompare);
      }
      if (discount) {
        const showDiscount = variant.discount_text && variant.discount_text.length > 0;
        discount.textContent = showDiscount ? variant.discount_text : '';
        discount.classList.toggle('is-hidden', !showDiscount);
      }
      if (installment) {
        const installmentText = installment.querySelector('[data-installment-text]');
        if (installmentText) {
          installmentText.textContent = (variant.installment_text || '').replace(/\s*Shop Pay$/i, '').replace(/\s+with\s*$/, ' with');
        } else {
          installment.textContent = (variant.installment_text || '').replace(/\s*Shop Pay$/i, '').replace(/\s+with\s*$/, ' with');
        }
        const mark = root.querySelector('[data-shop-pay-mark]');
        if (mark && !mark.querySelector('svg') && !mark.querySelector('span')) {
          injectShopPayMark(root);
        }
      }
      if (selectedLabel) selectedLabel.textContent = variant.title || getSelectedValue() || '';
      var inlineLabel = form.querySelector('[data-option-selected-inline]');
      if (inlineLabel) inlineLabel.textContent = variant.title || getSelectedValue() || '';
      var skuEl = root.querySelector('[data-variant-sku]');
      if (skuEl) skuEl.textContent = 'SKU: ' + (variant.sku || '');
      if (addButton) {
        addButton.disabled = !variant.available;
        const lockSvg = addButton.querySelector('.cpdp-panel__lock-icon');
        const lockHtml = lockSvg ? lockSvg.outerHTML + ' ' : '';
        addButton.innerHTML = `${lockHtml}${primaryLabel} \u2013 ${variant.price_text}`;
      }
      if (mobileAdd) mobileAdd.disabled = !variant.available;
      if (mobilePrice) mobilePrice.textContent = variant.price_text;
      heroApi.updateForVariant(variant);
    };

    optionInputs.forEach((input) => input.addEventListener('change', () => update(findVariant())));

    if (mobileBar && addButton && !mobileBar.classList.contains('is-disabled') && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(([entry]) => {
        mobileBar.classList.toggle('is-hidden', entry.isIntersecting);
      }, { threshold: 0.25 });
      observer.observe(addButton);
    }

    update(findVariant());

    const buyNow = form.querySelector('[data-buy-now]');
    /* Intercept form submit — AJAX add to cart + open mini cart */
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const formData = new FormData(form);
      if (addButton) {
        addButton.disabled = true;
        addButton.style.opacity = '0.6';
      }
      fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      })
      .then(r => r.json())
      .then(() => {
        if (addButton) {
          addButton.disabled = false;
          addButton.style.opacity = '';
        }
        /* Open mini cart */
        const openBtn = document.querySelector('[data-minicart-open]');
        if (openBtn) openBtn.click();
      })
      .catch(() => {
        if (addButton) {
          addButton.disabled = false;
          addButton.style.opacity = '';
        }
      });
    });

    if (buyNow) {
      buyNow.addEventListener('click', () => {
        const formData = new FormData(form);
        fetch('/cart/add.js', { method: 'POST', body: formData })
          .then(() => { window.location.href = '/checkout'; })
          .catch(() => { window.location.href = '/checkout'; });
      });
    }
  };

  const initLaunchers = (root) => {
    root.querySelectorAll('[data-chip-launch]').forEach((button) => {
      button.addEventListener('click', () => {
        const type = button.dataset.chipLaunch;
        if (type === 'wall-preview') {
          openWallPreview();
        } else if (type === 'perfect-match') {
          window.open('https://www.insigneartdesign.com/pages/art-studio', '_blank');
        } else {
          window.dispatchEvent(new CustomEvent('insigne:widget-launch', { detail: { type, sectionId: root.dataset.sectionId } }));
        }
      });
    });
  };

  /* FAQ reveal animation on scroll */
  function initFaqReveal() {
    const faqSection = document.querySelector('.cpdp-section--faq');
    if (!faqSection) return;
    /* Split-flap chars */
    const splitChars = faqSection.querySelectorAll('.cpdp-faq-split__char');
    /* Cards + form + viewall */
    const revealEls = [
      ...faqSection.querySelectorAll('.cpdp-faq-header__viewall'),
      ...faqSection.querySelectorAll('.cpdp-faq-card'),
      ...faqSection.querySelectorAll('.cpdp-faq-ask')
    ];
    let faqTriggered = false;
    const faqObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !faqTriggered) {
          faqTriggered = true;
          /* Drop chars with stagger */
          splitChars.forEach((ch, i) => {
            setTimeout(() => ch.classList.add('is-dropped'), i * 180);
          });
          /* Reveal cards after chars finish */
          const charDelay = splitChars.length * 180 + 200;
          revealEls.forEach((el, i) => {
            const isCard = el.classList.contains('cpdp-faq-card');
            const cardIdx = isCard ? Array.from(faqSection.querySelectorAll('.cpdp-faq-card')).indexOf(el) : -1;
            const delay = charDelay + (cardIdx >= 0 ? cardIdx * 80 : 0);
            setTimeout(() => el.classList.add('is-visible'), delay);
          });
          faqObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });
    faqObserver.observe(faqSection);
  }

  /* Review & Q&A tabs */
  function initReviewTabs() {
    const tabs = document.querySelectorAll('[data-review-tab]');
    if (!tabs.length) return;

    /**
     * Trigger Judge.me's internal Q&A tab so it renders Q&A content into the DOM.
     * Judge.me lazy-loads Q&A — elements like .jdgm-qna-widg only exist after
     * their internal tab button is clicked.
     */
    function triggerJudgemeQnaTab() {
      var btns = document.querySelectorAll('.jdgm-revs-tab-btn');
      for (var i = 0; i < btns.length; i++) {
        if (/question/i.test(btns[i].textContent)) {
          btns[i].click();
          return true;
        }
      }
      return false;
    }

    function triggerJudgemeReviewsTab() {
      var btns = document.querySelectorAll('.jdgm-revs-tab-btn');
      for (var i = 0; i < btns.length; i++) {
        if (/review/i.test(btns[i].textContent)) {
          btns[i].click();
          return true;
        }
      }
      return false;
    }

    /* Toggle .cpdp-qna-mode class on Judge.me widget */
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const target = tab.dataset.reviewTab;
        tabs.forEach((t) => t.classList.remove('cpdp-review-tab--active'));
        tab.classList.add('cpdp-review-tab--active');

        var widget = document.querySelector('#judgeme_product_reviews .jdgm-rev-widg');
        if (widget) {
          if (target === 'qna') {
            /* Trigger Judge.me internal Q&A tab to load content */
            if (!triggerJudgemeQnaTab()) {
              /* Judge.me may not be ready yet — observe and retry */
              var observer = new MutationObserver(function(mutations, obs) {
                if (triggerJudgemeQnaTab()) obs.disconnect();
              });
              observer.observe(widget, { childList: true, subtree: true });
              /* Safety timeout — stop observing after 8s */
              setTimeout(function() { observer.disconnect(); }, 8000);
            }
            widget.classList.add('cpdp-qna-mode');
          } else {
            widget.classList.remove('cpdp-qna-mode');
            triggerJudgemeReviewsTab();
          }
        }
      });
    });
    /* Hero star click smooth scroll */
    document.querySelectorAll('a[href="#cpdp-reviews"]').forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const sec = document.getElementById('cpdp-reviews');
        if (sec) sec.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
    /* Show "no reviews yet" if empty */
    window.addEventListener('jdgm.doneSetup', () => {
      const revs = document.querySelectorAll('.jdgm-rev:not(.jdgm-rev--form)');
      if (revs.length === 0) {
        const widget = document.querySelector('.cpdp-review-panel[data-review-panel="reviews"] .jdgm-rev-widg');
        if (widget) {
          const msg = document.createElement('p');
          msg.className = 'cpdp-no-reviews-msg';
          msg.textContent = 'No reviews yet — be the first to share your experience.';
          widget.insertBefore(msg, widget.firstChild);
        }
      }
      /* Hide "Ask a question" button inside Reviews tab — Q&A tab handles it */
      var revPanel = document.querySelector('.cpdp-review-panel[data-review-panel="reviews"]');
      if (revPanel) {
        revPanel.querySelectorAll('a, button').forEach(function(el) {
          if (/ask\s*(a\s*)?question/i.test(el.textContent)) {
            el.style.display = 'none';
          }
        });
      }
    });
  }

  /* Insigne AI Studio — Duvarında Gör (Wall Preview) launcher */
  function openWallPreview() {
    const p = window.__iasProduct || {};
    const productData = {
      handle: p.handle || '',
      title: p.title || '',
      pngUrl: p.pngUrl || '',
      imageUrl: p.imageUrl || '',
      price: p.price || '',
      currency: p.currency || 'USD',
      sku: p.sku || '',
      variantId: p.variantId || ''
    };
    try { sessionStorage.setItem('ias_dg_products', JSON.stringify([productData])); } catch (e) {}
    /* 1. App's duvarinda-gor element on page */
    const dgEl = document.querySelector('[data-duvarinda-gor]');
    if (dgEl) {
      document.dispatchEvent(new CustomEvent('ias:duvarinda-gor:open', { detail: { products: [productData] } }));
      return;
    }
    /* 2. Module 3 chat */
    if (window.InsigneModule3) { window.InsigneModule3.open(); return; }
    /* 3. Fallback — open art-studio page */
    window.open(window.location.origin + '/pages/art-studio?mode=wall', '_blank');
  }

  const injectSeenOnFallback = (root) => {
    const row = root.querySelector('[data-seen-on-row]');
    if (!row) return;
    const visibleChildren = Array.from(row.children).filter((node) => !node.hidden && (node.textContent || '').trim().length > 0);
    if (visibleChildren.length) return;
    const fallback = (row.dataset.wordmarkFallback || 'Forbes|Interior Design|HYPEBEAST|designboom').split('|');
    const classes = ['forbes', 'interior', 'hypebeast', 'designboom'];
    row.innerHTML = '';
    fallback.forEach((label, index) => {
      const span = document.createElement('span');
      span.className = `cpdp-wordmark cpdp-wordmark--${classes[index] || 'fallback'}`;
      span.textContent = label;
      row.appendChild(span);
    });
  };

  const bottomBandSvg = {
    glass: '<svg viewBox="0 0 36 36" role="presentation" focusable="false"><path d="M18 4 L28 12 L28 26 L18 32 L8 26 L8 12 Z" fill="none" stroke="currentColor" stroke-linejoin="round" stroke-width="1.5"/><path d="M18 4 L18 32 M8 12 L28 12 M8 26 L28 26 M8 12 L18 20 L28 12" fill="none" stroke="currentColor" stroke-linejoin="round" stroke-width="1.2" opacity="0.5"/></svg>',
    uv: '<svg viewBox="0 0 36 36" role="presentation" focusable="false"><path d="M18 6 C18 6 12 14 12 20 C12 23.3 14.7 26 18 26 C21.3 26 24 23.3 24 20 C24 14 18 6 18 6Z" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"/><path d="M27 8 L28.5 11 L31.5 12 L28.5 13 L27 16 L25.5 13 L22.5 12 L25.5 11Z" fill="none" stroke="currentColor" stroke-linejoin="round" stroke-width="1.3"/><circle cx="8" cy="10" r="1.2" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M8 7 L8 8 M8 12 L8 13 M5 10 L6 10 M10 10 L11 10" fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="1.2"/></svg>',
    hang: '<svg viewBox="0 0 36 36" role="presentation" focusable="false"><rect x="8" y="14" width="20" height="16" rx="2" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M14 14 V10 A4 4 0 0 1 22 10 V14" fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="1.5"/><path d="M13 22 L16 25 L23 18" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.6"/></svg>'
  };

  const ensureBottomBandIcons = (root) => {
    root.querySelectorAll('[data-bottom-band-icon]').forEach((node) => {
      const type = node.getAttribute('data-bottom-band-icon');
      if (node.querySelector('svg')) return;
      if (!bottomBandSvg[type]) return;
      node.innerHTML = bottomBandSvg[type];
    });
  };

  const initRoot = (root) => {
    if (!root || root.dataset.cpdpReady === 'true') return;
    root.dataset.cpdpReady = 'true';
    document.body.classList.add(bodyClass);
    hideFloatingWidgets();
    cleanupQuestionMarks(root);
    injectSeenOnFallback(root);
    ensureBottomBandIcons(root);
    bindWallPreview(root);
    applyTitleLayout(root);
    applyRightPanelDesktopState(root);
    injectShopPayMark(root);
    initRail(root);
    const variants = parseJson(root.querySelector('[data-variant-json]')) || [];
    initModal(root);
    const heroApi = initHero(root, variants);
    initForm(root, heroApi);
    initLaunchers(root);
    initReviewTabs();
    initFaqReveal();
  };

  document.querySelectorAll(rootSelector).forEach(initRoot);
  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.matches?.(rootSelector) ? event.target : event.target.querySelector?.(rootSelector);
    initRoot(root);
  });

  hideFloatingWidgets();
  const observer = new MutationObserver(() => hideFloatingWidgets());
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();

/* Debug mode removed — was only used during development */
(function cinematicDebugRemoved() {
  return;

  var active = true;
  root.classList.add('cpdp--debug');

  var toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.textContent = 'DEBUG ON';
  toggle.style.cssText = 'position:fixed;top:16px;right:16px;z-index:9999;height:36px;padding:0 12px;border-radius:999px;background:rgba(0,0,0,0.72);border:1px solid rgba(255,255,255,0.14);color:#F1E8DE;font-size:12px;font-weight:700;letter-spacing:0.06em;backdrop-filter:blur(10px);cursor:pointer;';
  document.body.appendChild(toggle);

  var mouseBox = document.createElement('div');
  mouseBox.style.cssText = 'position:fixed;right:16px;bottom:16px;z-index:9999;padding:6px 12px;border-radius:999px;background:rgba(0,0,0,0.72);border:1px solid rgba(255,255,255,0.14);color:#F1E8DE;font-size:11px;font-weight:600;letter-spacing:0.04em;backdrop-filter:blur(10px);pointer-events:none;';
  mouseBox.textContent = 'X: 0  Y: 0';
  document.body.appendChild(mouseBox);

  toggle.addEventListener('click', function () {
    active = !active;
    toggle.textContent = active ? 'DEBUG ON' : 'DEBUG OFF';
    root.classList.toggle('cpdp--debug', active);
    mouseBox.style.display = active ? '' : 'none';
  });

  document.addEventListener('mousemove', function (e) {
    if (!active) return;
    mouseBox.textContent = 'X: ' + e.clientX + '  Y: ' + e.clientY;
  });

  function label(v) { return v.replace(/-/g, ' ').toUpperCase(); }

  root.querySelectorAll('[data-cinematic]').forEach(function (block) {
    var name = block.getAttribute('data-cinematic');
    var pos = getComputedStyle(block).position;
    if (pos === 'static') block.style.position = 'relative';

    var lbl = document.createElement('span');
    lbl.className = 'cpdp-debug-label';
    lbl.textContent = label(name);
    block.appendChild(lbl);

    var info = document.createElement('div');
    info.className = 'cpdp-debug-info';
    info.innerHTML = '<div class="cpdp-debug-info__title"></div><div class="cpdp-debug-info__metrics"></div>';
    block.appendChild(info);

    block.addEventListener('mouseenter', function () {
      if (!active) return;
      var r = block.getBoundingClientRect();
      var vw = window.innerWidth;
      var vh = window.innerHeight;
      var z = getComputedStyle(block).zIndex;
      info.querySelector('.cpdp-debug-info__title').textContent = 'BLOCK: ' + label(name);
      info.querySelector('.cpdp-debug-info__metrics').innerHTML =
        '<span>W: ' + Math.round(r.width) + 'px</span>' +
        '<span>H: ' + Math.round(r.height) + 'px</span>' +
        '<span>X: ' + Math.round(r.left) + 'px</span>' +
        '<span>Y: ' + Math.round(r.top) + 'px</span>' +
        '<span>TOP: ' + Math.round(r.top) + 'px</span>' +
        '<span>LEFT: ' + Math.round(r.left) + 'px</span>' +
        '<span>RIGHT: ' + Math.round(vw - r.right) + 'px</span>' +
        '<span>BOTTOM: ' + Math.round(vh - r.bottom) + 'px</span>' +
        '<span>Z: ' + z + '</span>';
    });
  });
})();

/* ── Scroll hijack: DISABLED — no inner scroll trapping ── */
(function(){
  return; /* disabled: user wants natural page scroll everywhere */
  var panel, hero;
  function init(){
    var outer = document.getElementById('cinematic-right-panel');
    panel = outer ? outer.querySelector('.cpdp-panel') : null;
    hero = document.querySelector('.cpdp__hero');
    if(!panel || !hero) return;
    window.addEventListener('wheel', onWheel, {passive: false});
  }
  function onWheel(e){
    if(!panel) return;
    var heroRect = hero.getBoundingClientRect();
    /* Only hijack when hero is in view */
    if(heroRect.bottom <= 0 || heroRect.top >= window.innerHeight) return;
    var atTop = panel.scrollTop <= 0;
    var atBottom = panel.scrollTop + panel.clientHeight >= panel.scrollHeight - 2;
    if(e.deltaY > 0 && !atBottom){
      e.preventDefault();
      panel.scrollBy({top: e.deltaY, behavior: 'smooth'});
    } else if(e.deltaY < 0 && !atTop && window.scrollY <= heroRect.height){
      e.preventDefault();
      panel.scrollBy({top: e.deltaY, behavior: 'smooth'});
    }
  }
  if(document.readyState === 'loading'){
    document.addEventListener('DOMContentLoaded', init);
  } else {
    setTimeout(init, 500);
  }
})();

/* ── Prevent theme/apps from locking body scroll on cinematic page ── */
(function(){
  if(!document.body.classList.contains('cpdp-page')) return;
  /* Kill the popup overlay immediately on cinematic pages */
  var popup = document.getElementById('insigneUniversalOverlay');
  if(popup) popup.remove();
  /* Continuous guard: ensure body scroll is never locked */
  function unlockScroll(){
    if(document.body.style.overflow === 'hidden') document.body.style.overflow = '';
    if(document.documentElement.style.overflow === 'hidden') document.documentElement.style.overflow = '';
  }
  unlockScroll();
  setInterval(unlockScroll, 300);
  /* Also watch for dynamically added overlays */
  var mo = new MutationObserver(function(){
    var p = document.getElementById('insigneUniversalOverlay');
    if(p) p.remove();
    unlockScroll();
  });
  mo.observe(document.body, { childList: true, subtree: false, attributes: true, attributeFilter: ['style'] });
})();

/* ── Kill theme header elements that create top spacing ── */
(function(){
  if(!document.body.classList.contains('cpdp-page')) return;
  var selectors = [
    '.shopify-section-group-header-group',
    '#shopify-section-header',
    '.wrapper-header',
    '.halo-sidebar',
    '.announcement-bar',
    '[id*="shopify-section-announcement"]'
  ];
  selectors.forEach(function(sel){
    document.querySelectorAll(sel).forEach(function(el){
      el.style.cssText = 'display:none!important;height:0!important;margin:0!important;padding:0!important;';
    });
  });
  /* Also force body to have no top spacing */
  document.body.style.marginTop = '0';
  document.body.style.paddingTop = '0';
})();

/* ── Scroll Reveal: IntersectionObserver ── */
(function(){
  function initReveal(){
    var els = document.querySelectorAll('.cpdp-reveal');
    if(!els.length) return;
    if(!window.IntersectionObserver){
      els.forEach(function(el){ el.classList.add('is-visible'); });
      return;
    }
    var obs = new IntersectionObserver(function(entries){
      entries.forEach(function(entry){
        if(entry.isIntersecting){
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });
    els.forEach(function(el){ obs.observe(el); });
  }
  if(document.readyState === 'loading'){
    document.addEventListener('DOMContentLoaded', initReveal);
  } else {
    setTimeout(initReveal, 300);
  }
})();

/* ── Geometric shapes parallax on scroll — DISABLED (causes scroll jank) ── */
(function(){
  return;
  var shapes, ticking = false;
  function initGeo(){
    shapes = document.querySelectorAll('.cpdp-geo__shape');
    if(!shapes.length) return;
    window.addEventListener('scroll', onScroll, {passive: true});
  }
  function onScroll(){
    if(!ticking){
      ticking = true;
      requestAnimationFrame(updateGeo);
    }
  }
  function updateGeo(){
    ticking = false;
    var scrollY = window.scrollY;
    shapes.forEach(function(shape, i){
      var speed = 0.02 + (i * 0.015);
      var y = scrollY * speed;
      var scale = 1 + (scrollY * 0.00005 * (i % 2 === 0 ? 1 : -1));
      shape.style.transform = 'translateY(' + y + 'px) scale(' + scale + ')' + (shape.style.transform.indexOf('rotate') > -1 ? '' : '');
    });
  }
  if(document.readyState === 'loading'){
    document.addEventListener('DOMContentLoaded', initGeo);
  } else {
    setTimeout(initGeo, 300);
  }
})();

/* ── Quantity selector with price update ── */
(function(){
  function updateCartPrice(){
    var input = document.querySelector('[data-qty-input]');
    if(!input) return;
    var qty = parseInt(input.value) || 1;
    var priceEl = document.querySelector('[data-price]');
    if(!priceEl) return;
    /* Get current variant price from the variant JSON */
    var vidInput = document.querySelector('[data-variant-id]');
    if(!vidInput) return;
    var vid = parseInt(vidInput.value);
    var jsonEl = document.querySelector('[data-variant-json]');
    if(!jsonEl) return;
    try {
      var variants = JSON.parse(jsonEl.textContent.trim());
      var v = variants.find(function(x){ return x.id === vid; });
      if(!v) return;
      var unitPrice = v.price;
      var totalPrice = unitPrice * qty;
      /* Format price */
      var formatted = (totalPrice / 100).toLocaleString('en-US', {style:'currency', currency:'USD'});
      /* Update ADD TO CART button text */
      var cartBtn = document.querySelector('[data-add-to-cart]');
      if(cartBtn){
        var svg = cartBtn.querySelector('svg');
        var svgHtml = svg ? svg.outerHTML : '';
        cartBtn.innerHTML = svgHtml + ' ADD TO CART \u2014 ' + formatted;
      }
    } catch(ex){}
  }
  document.addEventListener('click', function(e){
    var btn = e.target.closest('[data-qty-change]');
    if(!btn) return;
    var wrap = btn.closest('.cpdp-qty');
    if(!wrap) return;
    var input = wrap.querySelector('[data-qty-input]');
    if(!input) return;
    var val = parseInt(input.value) || 1;
    var delta = parseInt(btn.dataset.qtyChange) || 0;
    var min = parseInt(input.min) || 1;
    var max = parseInt(input.max) || 99;
    val = Math.max(min, Math.min(max, val + delta));
    input.value = val;
    updateCartPrice();
  });
  /* Also update on manual input change */
  document.addEventListener('change', function(e){
    if(e.target.matches('[data-qty-input]')) updateCartPrice();
  });
  /* Reset qty to 1 when variant changes, then update price */
  document.addEventListener('change', function(e){
    if(e.target.matches('[data-option-input]')){
      var input = document.querySelector('[data-qty-input]');
      if(input) input.value = 1;
      setTimeout(updateCartPrice, 100);
    }
  });
  /* Expose for external calls */
  window.__cpdpUpdateCartPrice = updateCartPrice;
})();

/* ── Hamburger menu toggle ── */
(function(){
  var burger = document.querySelector('[data-menu-toggle]');
  var nav = document.querySelector('[data-menu-nav]');
  if(!burger || !nav) return;
  function closeMenu(){
    burger.classList.remove('is-open');
    nav.classList.remove('is-open');
    burger.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }
  burger.addEventListener('click', function(){
    var open = burger.classList.toggle('is-open');
    nav.classList.toggle('is-open');
    burger.setAttribute('aria-expanded', open);
    document.body.style.overflow = open ? 'hidden' : '';
  });
  var closeBtns = nav.querySelectorAll('[data-menu-close]');
  closeBtns.forEach(function(btn){ btn.addEventListener('click', closeMenu); });
  /* Close on link click inside mobile menu */
  var mobileMenu = nav.querySelector('[data-mobile-menu]');
  if(mobileMenu){
    mobileMenu.addEventListener('click', function(e){
      if(e.target.tagName === 'A') closeMenu();
    });
  }
  /* Accordion toggle */
  nav.querySelectorAll('[data-accordion-trigger]').forEach(function(trigger){
    trigger.addEventListener('click', function(){
      var group = trigger.closest('[data-accordion]');
      if(!group) return;
      var wasOpen = group.classList.contains('is-open');
      /* Close all others */
      nav.querySelectorAll('[data-accordion].is-open').forEach(function(g){
        if(g !== group) g.classList.remove('is-open');
      });
      group.classList.toggle('is-open', !wasOpen);
    });
  });
})();

/* ── Account dropdown toggle ── */
(function(){
  var trigger = document.querySelector('[data-caccount-trigger]');
  var dropdown = document.querySelector('[data-caccount-dropdown]');
  if(!trigger || !dropdown) return;
  trigger.addEventListener('click', function(e){
    e.stopPropagation();
    var open = dropdown.hidden;
    dropdown.hidden = !open;
  });
  document.addEventListener('click', function(e){
    if(!dropdown.hidden && !dropdown.contains(e.target) && e.target !== trigger){
      dropdown.hidden = true;
    }
  });
})();

/* ── Scroll header: big at top (64px), small on scroll (44px) ── */
(function(){
  var header = document.getElementById('cinematic-header');
  if(!header) return;
  var scrolled = false;
  function check(){
    if(window.innerWidth >= 1200){ header.classList.remove('is-scrolled'); return; }
    var isScrolled = window.scrollY > 20;
    if(isScrolled !== scrolled){
      scrolled = isScrolled;
      header.classList.toggle('is-scrolled', scrolled);
    }
  }
  check();
  window.addEventListener('scroll', check, {passive: true});
  window.addEventListener('resize', check, {passive: true});
})();

/* ── Mini Cart Drawer ── */
(function(){
  /* ═══ DISCOUNT TIERS — from theme settings (editable in Shopify admin) ═══ */
  var TIERS = [];
  try {
    var tiersJson = document.querySelector('[data-discount-tiers]');
    if(tiersJson) TIERS = JSON.parse(tiersJson.textContent.trim());
  } catch(e){}
  /* Fallback if no settings */
  if(!TIERS.length) TIERS = [{ minQty: 2, percent: 10 }, { minQty: 3, percent: 15 }, { minQty: 4, percent: 20 }];

  var mc = document.querySelector('[data-minicart]');
  if(!mc) return;
  var openBtns = document.querySelectorAll('[data-minicart-open]');
  var closeBtns = mc.querySelectorAll('[data-minicart-close]');
  var body = mc.querySelector('[data-minicart-body]');
  var itemsEl = mc.querySelector('[data-minicart-items]');
  var emptyEl = mc.querySelector('[data-minicart-empty]');
  var subtotalEl = mc.querySelector('[data-minicart-subtotal]');
  var badgeEl = mc.querySelector('[data-minicart-badge]');
  var countEls = document.querySelectorAll('[data-minicart-count]');

  function open(){
    mc.hidden = false;
    requestAnimationFrame(function(){
      requestAnimationFrame(function(){ mc.classList.add('is-open'); });
    });
    fetchCart();
  }
  function close(){
    mc.classList.remove('is-open');
    setTimeout(function(){ mc.hidden = true; }, 350);
  }

  openBtns.forEach(function(b){ b.addEventListener('click', function(e){ e.preventDefault(); open(); }); });
  closeBtns.forEach(function(b){ b.addEventListener('click', close); });
  document.addEventListener('keydown', function(e){ if(e.key === 'Escape' && !mc.hidden) close(); });

  function money(cents){ var n = parseFloat(cents) || 0; return '$' + (n / 100).toFixed(2); }
  function esc(s){ var d=document.createElement('div'); d.textContent=s; return d.innerHTML; }

  function fetchCart(){
    fetch('/cart.js').then(function(r){ return r.json(); }).then(render).catch(function(){});
  }

  function render(cart){
    var count = cart.item_count;
    badgeEl.textContent = count;
    countEls.forEach(function(el){ el.textContent = 'Cart (' + count + ')'; });
    mc.setAttribute('data-empty', count === 0);
    /* Clear promo message on every cart update */
    var _promoMsg = mc.querySelector('[data-promo-msg]');
    if(_promoMsg){ _promoMsg.textContent = ''; _promoMsg.className = 'minicart__promo-msg'; }
    if(count === 0){
      emptyEl.style.display = '';
      itemsEl.innerHTML = '';
      subtotalEl.textContent = money(0);
      return;
    }
    emptyEl.style.display = 'none';
    /* ═══ DISCOUNT ANALYSIS — separate auto vs manual discounts ═══ */
    var autoDiscounts = []; /* Shopify automatic discounts (type: automatic) */
    var codeDiscounts = []; /* Manual discount codes (type: discount_code) */
    var allSources = (cart.cart_level_discount_applications || []).concat([]);
    /* Also scan line-level */
    (cart.items || []).forEach(function(item){
      (item.line_level_discount_allocations || []).forEach(function(d){
        if(d.discount_application) allSources.push(d.discount_application);
      });
    });
    /* Deduplicate and categorize */
    var seen = {};
    allSources.forEach(function(d){
      var key = (d.title || '').toLowerCase();
      if(seen[key]) return;
      seen[key] = true;
      var entry = { title: d.title || 'Discount', type: d.type || 'automatic' };
      if(d.type === 'discount_code') codeDiscounts.push(entry);
      else autoDiscounts.push(entry);
    });
    var cartDiscountTotal = cart.total_discount || 0;
    var hasManualCode = codeDiscounts.length > 0;
    /* Subtotal — strikethrough only when discount applied */
    if(cartDiscountTotal > 0){
      subtotalEl.innerHTML = '<span class="mc-price-orig">' + money(cart.total_price + cartDiscountTotal) + '</span> ' + money(cart.total_price);
    } else {
      subtotalEl.textContent = money(cart.total_price);
    }
    /* Discount tiers — only show when TIERS configured and auto-discount active or close to one */
    var tiersEl = mc.querySelector('[data-minicart-tiers]');
    if(tiersEl && TIERS.length){
      var qty = cart.item_count;
      /* Find current and next tier */
      var currentTier = null, nextTier = null, currentIdx = -1;
      for(var i = TIERS.length - 1; i >= 0; i--){
        if(qty >= TIERS[i].minQty){ currentTier = TIERS[i]; currentIdx = i; nextTier = TIERS[i + 1] || null; break; }
      }
      if(!currentTier) nextTier = TIERS[0] || null;

      var html = '<div class="mc-tiers">';
      /* Tier steps */
      html += '<div class="mc-tiers__steps">';
      TIERS.forEach(function(t, idx){
        var active = currentIdx >= 0 && idx <= currentIdx;
        var isCurrent = idx === currentIdx;
        html += '<div class="mc-tiers__step' + (active ? ' is-active' : '') + (isCurrent ? ' is-current' : '') + '">';
        html += '<span class="mc-tiers__dot"></span>';
        html += '<span class="mc-tiers__label">' + t.minQty + '+ items</span>';
        html += '<span class="mc-tiers__pct">' + t.percent + '% OFF</span>';
        html += '</div>';
      });
      html += '</div>';
      /* Progress bar — center-aligned to tier dot positions */
      var segments = TIERS.length;
      var progress = 0;
      if(currentIdx >= 0){
        if(currentIdx === segments - 1){
          progress = 100;
        } else {
          progress = ((currentIdx + 0.5) / segments) * 100;
        }
      }
      /* No fill if no tier reached — bar stays empty */
      progress = Math.min(100, Math.max(0, progress));
      html += '<div class="mc-tiers__bar"><div class="mc-tiers__fill" style="width:' + progress + '%"></div></div>';
      /* Discount messages — clear and specific */
      if(cartDiscountTotal > 0){
        var allNames = autoDiscounts.concat(codeDiscounts);
        allNames.forEach(function(d){
          var icon = d.type === 'discount_code' ? '&#127380;' : '&#127881;';
          var label = d.type === 'discount_code' ? 'Code' : 'Auto';
          html += '<div class="mc-tiers__msg mc-tiers__msg--success">' + icon + ' <strong>' + d.title + '</strong>';
          if(d.type === 'discount_code') html += ' <button type="button" class="mc-tiers__remove" data-remove-discount>remove</button>';
          html += '</div>';
        });
        html += '<div class="mc-tiers__msg mc-tiers__msg--success">Total savings: <strong>' + money(cartDiscountTotal) + '</strong></div>';
      }
      if(nextTier){
        var need = nextTier.minQty - qty;
        html += '<div class="mc-tiers__msg mc-tiers__msg--next">Add ' + need + ' more item' + (need > 1 ? 's' : '') + ' to unlock <strong>' + nextTier.percent + '% OFF</strong></div>';
      } else if(currentTier && currentIdx === TIERS.length - 1){
        html += '<div class="mc-tiers__msg mc-tiers__msg--max">&#10024; Maximum discount unlocked!</div>';
      }
      html += '</div>';
      tiersEl.innerHTML = html;
      tiersEl.style.display = '';
      /* Remove discount handler */
      var removeBtn = tiersEl.querySelector('[data-remove-discount]');
      if(removeBtn){
        removeBtn.onclick = function(e){
          e.preventDefault();
          removeBtn.textContent = '...';
          /* Clear discount by posting empty code via cart update */
          fetch('/cart/update.js', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({ discount: '' })
          }).then(function(){
            /* Also hit /discount/ with empty to clear session */
            return fetch('/discount/', { redirect: 'follow' });
          }).then(function(){
            return fetch('/cart.js');
          }).then(function(r){ return r.json(); }).then(function(cart){
            render(cart);
            if(checkoutLink) checkoutLink.href = '/checkout';
            if(promoInput) promoInput.value = '';
            if(promoMsg){ promoMsg.textContent = 'Discount removed'; promoMsg.className = 'minicart__promo-msg'; }
          });
        };
      }
    } else if(tiersEl){
      tiersEl.style.display = 'none';
    }
    var html = '';
    cart.items.forEach(function(item){
      var imgUrl = item.image ? item.image.replace(/(\.\w+)(\?|$)/, '_180x$1$2') : '';
      html += '<div class="minicart__item" data-line-key="' + item.key + '">' +
        '<img src="' + imgUrl + '" class="minicart__item-img" alt="" width="64" height="64" loading="lazy">' +
        '<div class="minicart__item-info">' +
          '<a href="' + item.url + '" class="minicart__item-title">' + item.product_title + '</a>' +
          '<span class="minicart__item-variant">' + (item.variant_title || '') + '</span>' +
          '<div class="minicart__item-row">' +
            '<div class="minicart__qty">' +
              '<button type="button" class="minicart__qty-btn" data-mc-qty="' + item.key + '" data-mc-delta="-1">&minus;</button>' +
              '<span class="minicart__qty-val">' + item.quantity + '</span>' +
              '<button type="button" class="minicart__qty-btn" data-mc-qty="' + item.key + '" data-mc-delta="1">&plus;</button>' +
            '</div>' +
            '<span class="minicart__item-price">' + money(item.final_line_price) + '</span>' +
            '<button type="button" class="minicart__item-remove" data-mc-remove="' + item.key + '" aria-label="Remove"><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg></button>' +
          '</div>' +
        '</div>' +
      '</div>';
    });
    itemsEl.innerHTML = html;
  }

  /* Qty change + remove via event delegation — debounced */
  var qtyTimers = {};
  mc.addEventListener('click', function(e){
    var qtyBtn = e.target.closest('[data-mc-qty]');
    var removeBtn = e.target.closest('[data-mc-remove]');
    if(qtyBtn){
      var key = qtyBtn.dataset.mcQty;
      var delta = parseInt(qtyBtn.dataset.mcDelta);
      var valEl = qtyBtn.parentElement.querySelector('.minicart__qty-val');
      var newQty = Math.max(0, parseInt(valEl.textContent) + delta);
      valEl.textContent = newQty;
      /* Debounce: wait 400ms after last click before sending */
      clearTimeout(qtyTimers[key]);
      qtyTimers[key] = setTimeout(function(){ changeQty(key, newQty); }, 400);
    }
    if(removeBtn){
      var key2 = removeBtn.dataset.mcRemove;
      var item = removeBtn.closest('.minicart__item');
      if(item) item.style.opacity = '0.3';
      clearTimeout(qtyTimers[key2]);
      changeQty(key2, 0);
    }
  });

  var pending = false;
  var queued = null;
  function changeQty(key, qty){
    if(pending){ queued = {key: key, qty: qty}; return; }
    pending = true;
    fetch('/cart/change.js', {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({ id: key, quantity: qty })
    }).then(function(r){ return r.json(); }).then(function(cart){
      pending = false;
      render(cart);
      if(queued){ var q = queued; queued = null; changeQty(q.key, q.qty); }
      else { setTimeout(fetchCart, 400); } /* Re-fetch to get updated discount state */
    }).catch(function(){
      pending = false;
      queued = null;
      fetchCart();
    });
  }

  /* Promo code — appends discount to checkout URL */
  var promoInput = mc.querySelector('[data-promo-input]');
  var promoBtn = mc.querySelector('[data-promo-apply]');
  var promoMsg = mc.querySelector('[data-promo-msg]');
  var checkoutLink = mc.querySelector('[data-minicart-checkout]');
  if(promoBtn && promoInput && checkoutLink){
    function applyPromo(){
      var code = promoInput.value.trim();
      if(!code){
        promoMsg.textContent = 'Please enter a code';
        promoMsg.className = 'minicart__promo-msg is-error';
        return;
      }
      promoMsg.textContent = 'Checking...';
      promoMsg.className = 'minicart__promo-msg';
      promoBtn.disabled = true;

      var beforeDiscount = 0;
      var beforeNames = [];

      fetch('/cart.js').then(function(r){ return r.json(); }).then(function(cartBefore){
        beforeDiscount = cartBefore.total_discount || 0;
        (cartBefore.cart_level_discount_applications || []).forEach(function(d){ if(d.title) beforeNames.push(d.title.toLowerCase()); });
        (cartBefore.items || []).forEach(function(item){
          (item.line_level_discount_allocations || []).forEach(function(d){
            if(d.discount_application && d.discount_application.title) beforeNames.push(d.discount_application.title.toLowerCase());
          });
        });
        return fetch('/discount/' + encodeURIComponent(code), { method: 'GET', redirect: 'follow' });
      }).then(function(){
        return fetch('/cart.js');
      }).then(function(r){ return r.json(); }).then(function(cartAfter){
        promoBtn.disabled = false;
        var afterDiscount = cartAfter.total_discount || 0;
        var afterNames = [];
        (cartAfter.cart_level_discount_applications || []).forEach(function(d){ if(d.title) afterNames.push(d.title.toLowerCase()); });
        (cartAfter.items || []).forEach(function(item){
          (item.line_level_discount_allocations || []).forEach(function(d){
            if(d.discount_application && d.discount_application.title) afterNames.push(d.discount_application.title.toLowerCase());
          });
        });

        var codeInAfter = afterNames.indexOf(code.toLowerCase()) !== -1;
        var codeWasAlreadyThere = beforeNames.indexOf(code.toLowerCase()) !== -1;

        if(codeInAfter && !codeWasAlreadyThere){
          /* Code accepted */
          checkoutLink.href = '/checkout?discount=' + encodeURIComponent(code);
          promoMsg.innerHTML = '<strong>' + esc(code.toUpperCase()) + '</strong> applied! Saving ' + money(afterDiscount);
          promoMsg.className = 'minicart__promo-msg is-success';
          render(cartAfter);
        } else if(codeWasAlreadyThere){
          promoMsg.textContent = 'This code is already applied';
          promoMsg.className = 'minicart__promo-msg is-success';
        } else if(!codeInAfter && beforeDiscount > 0){
          /* Code not applied — existing discount active */
          var existingName = beforeNames.length ? beforeNames[0] : 'current discount';
          promoMsg.innerHTML = 'Not applied — you already have <strong>' + esc(existingName) + '</strong> saving you ' + money(beforeDiscount) + '. Our system always applies the best discount for you.';
          promoMsg.className = 'minicart__promo-msg is-error';
        } else {
          promoMsg.textContent = 'Invalid or expired discount code';
          promoMsg.className = 'minicart__promo-msg is-error';
        }
      }).catch(function(){
        promoBtn.disabled = false;
        promoMsg.textContent = 'Could not verify code. Try again.';
        promoMsg.className = 'minicart__promo-msg is-error';
      });
    }
    promoBtn.addEventListener('click', applyPromo);
    promoInput.addEventListener('keydown', function(e){ if(e.key === 'Enter') applyPromo(); });
  }

  /* Auto-refresh after add to cart */
  document.addEventListener('cart:updated', function(){ if(!mc.hidden) fetchCart(); });
})();

/* ── Live Search ── */
(function(){
  var wrap = document.querySelector('[data-csearch]');
  if(!wrap) return;
  var trigger = wrap.querySelector('[data-csearch-open]');
  var panel = wrap.querySelector('[data-csearch-panel]');
  var backdrop = wrap.querySelector('[data-csearch-close]');
  var input = wrap.querySelector('[data-csearch-input]');
  var results = wrap.querySelector('[data-csearch-results]');
  var timer = null;
  var cache = {};

  function open(){
    panel.hidden = false;
    if(backdrop) backdrop.hidden = false;
    input.value = '';
    results.innerHTML = '';
    document.body.style.overflow = 'hidden';
    setTimeout(function(){ input.focus(); }, 60);
  }
  function close(){
    panel.hidden = true;
    if(backdrop) backdrop.hidden = true;
    input.blur();
    document.body.style.overflow = '';
  }

  trigger.addEventListener('click', open);
  if(backdrop) backdrop.addEventListener('click', close);
  /* Close when clicking overlay background (not form/results) */
  panel.addEventListener('click', function(e){
    if(e.target === panel) close();
  });
  document.addEventListener('keydown', function(e){
    if(e.key === 'Escape' && !panel.hidden) close();
    /* Ctrl+K or Cmd+K to open search */
    if((e.ctrlKey || e.metaKey) && e.key === 'k'){
      e.preventDefault();
      panel.hidden ? open() : close();
    }
  });

  input.addEventListener('input', function(){
    clearTimeout(timer);
    var q = input.value.trim();
    if(q.length < 2){ results.innerHTML = ''; return; }
    timer = setTimeout(function(){ doSearch(q); }, 250);
  });

  function doSearch(q){
    if(cache[q]){ render(cache[q], q); return; }
    results.innerHTML = '<div class="csearch__loading">Searching...</div>';
    fetch('/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=6')
      .then(function(r){ return r.json(); })
      .then(function(data){
        var products = (data.resources && data.resources.results && data.resources.results.products) || [];
        cache[q] = products;
        render(products, q);
      })
      .catch(function(){
        results.innerHTML = '<div class="csearch__empty">Search unavailable</div>';
      });
  }

  function render(products, q){
    if(!products.length){
      results.innerHTML = '<div class="csearch__empty"><strong>No results</strong>Try a different search term</div>';
      return;
    }
    var html = '';
    products.forEach(function(p){
      var img = p.image || p.featured_image || '';
      var imgUrl = typeof img === 'string' ? img : (img.url || '');
      if(imgUrl && imgUrl.indexOf('?') === -1) imgUrl += '?width=96';
      var pMin = parseFloat(p.price_min || p.price || '0');
      var pMax = parseFloat(p.price_max || p.price || '0');
      var price = '';
      if(pMin > 0){
        price = (pMin !== pMax && pMax > pMin) ? 'From $' + pMin.toFixed(2) : '$' + pMin.toFixed(2);
      }
      var shortTitle = p.title.length > 40 ? p.title.substring(0, 40) + '...' : p.title;
      var escTitle = shortTitle.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
      var escType = (p.type || 'Product').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
      var escUrl = p.url.replace(/"/g,'&quot;');
      html += '<a href="' + escUrl + '" class="csearch__item">' +
        (imgUrl ? '<img src="' + imgUrl + '" class="csearch__item-img" alt="" loading="lazy" width="52" height="52">' : '') +
        '<div class="csearch__item-info"><div class="csearch__item-title">' + escTitle + '</div>' +
        '<div class="csearch__item-type">' + escType + '</div></div>' +
        (price ? '<span class="csearch__item-price">' + price + '</span>' : '') +
        '</a>';
    });
    html += '<a href="/search?q=' + encodeURIComponent(q) + '" class="csearch__viewall">View all results &rarr;</a>';
    results.innerHTML = html;
  }
})();

/* ── Mobile swipe gallery: swipe hero to browse, tap to open modal ── */
(function(){
  var heroMedia = document.querySelector('.cpdp__hero-media');
  var heroImg = document.querySelector('.cpdp__hero-image');
  var dotsContainer = document.querySelector('[data-swipe-dots]');
  var imagesJson = document.querySelector('[data-swipe-images]');
  if(!heroMedia || !heroImg || !dotsContainer || !imagesJson) return;

  var images = [];
  try { images = JSON.parse(imagesJson.textContent); } catch(e){ return; }
  if(!images.length) return;

  var dots = Array.from(dotsContainer.querySelectorAll('[data-swipe-dot]'));
  var current = 0;
  var touchStartX = 0;
  var touchStartY = 0;
  var picture = heroImg.closest('picture');

  /* Save original hero sources for restoration */
  var origHeroSrc = heroImg.getAttribute('src');
  var origHeroSrcset = heroImg.getAttribute('srcset') || '';
  var origSources = [];
  if(picture){
    picture.querySelectorAll('source').forEach(function(s){
      origSources.push({ el: s, srcset: s.getAttribute('srcset') });
    });
  }

  /* Swipe edge arrows on hero image */
  var arrowSvgL = '<svg viewBox="0 0 14 14" role="presentation" focusable="false"><path d="M9 3.2 5.2 7 9 10.8" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"></path></svg>';
  var arrowSvgR = '<svg viewBox="0 0 14 14" role="presentation" focusable="false"><path d="M5 3.2 8.8 7 5 10.8" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"></path></svg>';

  var swipePrev = document.createElement('button');
  swipePrev.type = 'button';
  swipePrev.className = 'cpdp__swipe-arrow cpdp__swipe-arrow--prev';
  swipePrev.setAttribute('aria-label', 'Previous image');
  swipePrev.innerHTML = arrowSvgL;

  var swipeNext = document.createElement('button');
  swipeNext.type = 'button';
  swipeNext.className = 'cpdp__swipe-arrow cpdp__swipe-arrow--next';
  swipeNext.setAttribute('aria-label', 'Next image');
  swipeNext.innerHTML = arrowSvgR;

  heroMedia.appendChild(swipePrev);
  heroMedia.appendChild(swipeNext);

  function updateSwipeArrows(){
    /* Always show both arrows when more than 1 image */
    swipePrev.hidden = images.length <= 1;
    swipeNext.hidden = images.length <= 1;
  }

  swipePrev.addEventListener('click', function(e){ e.stopPropagation(); e.preventDefault(); goTo(current - 1); });
  swipeNext.addEventListener('click', function(e){ e.stopPropagation(); e.preventDefault(); goTo(current + 1); });

  /* Preload first 5 gallery images for instant swipe */
  if(window.innerWidth < 768){
    images.slice(0, 5).forEach(function(img){
      var link = document.createElement('link');
      link.rel = 'prefetch'; link.as = 'image'; link.href = img.src;
      document.head.appendChild(link);
    });
  }

  function goTo(idx){
    /* Loop: end → start, start → end */
    if(idx < 0) idx = images.length - 1;
    if(idx >= images.length) idx = 0;
    current = idx;
    var img = images[idx];
    heroImg.src = img.src;
    heroImg.srcset = img.src;
    if(picture){
      picture.querySelectorAll('source').forEach(function(s){ s.srcset = img.src; });
    }
    dots.forEach(function(d, i){ d.classList.toggle('is-active', i === idx); });
    updateSwipeArrows();
    /* Prefetch next image for smooth swipe */
    var nextIdx = idx + 1 < images.length ? idx + 1 : 0;
    var prevIdx = idx - 1 >= 0 ? idx - 1 : images.length - 1;
    [nextIdx, prevIdx].forEach(function(pi){
      if(images[pi] && !images[pi]._prefetched){
        var i = new Image(); i.src = images[pi].src;
        images[pi]._prefetched = true;
      }
    });
  }

  /* Restore original hero when leaving mobile width */
  function restoreHero(){
    current = 0;
    heroImg.src = origHeroSrc;
    if(origHeroSrcset) { heroImg.srcset = origHeroSrcset; }
    else { heroImg.removeAttribute('srcset'); }
    origSources.forEach(function(o){ o.el.srcset = o.srcset; });
    dots.forEach(function(d, i){ d.classList.toggle('is-active', i === 0); });
    updateSwipeArrows();
  }

  var wasMobile = window.innerWidth < 768;
  window.addEventListener('resize', function(){
    var isMobile = window.innerWidth < 768;
    if(wasMobile && !isMobile){
      restoreHero();
    }
    wasMobile = isMobile;
  });

  updateSwipeArrows();

  /* Swipe on hero area */
  heroMedia.addEventListener('touchstart', function(e){
    touchStartX = e.changedTouches[0].clientX;
    touchStartY = e.changedTouches[0].clientY;
  }, {passive: true});

  heroMedia.addEventListener('touchend', function(e){
    var dx = e.changedTouches[0].clientX - touchStartX;
    var dy = e.changedTouches[0].clientY - touchStartY;
    /* Only horizontal swipe (not vertical scroll) */
    if(Math.abs(dx) < 40 || Math.abs(dy) > Math.abs(dx)) {
      /* Tap (not swipe) — open gallery modal */
      if(Math.abs(dx) < 10 && Math.abs(dy) < 10){
        var mediaId = images[current]?.mediaId;
        if(mediaId){
          var trigger = document.querySelector('[data-gallery-open][data-media-id="' + mediaId + '"]');
          if(trigger) trigger.click();
        } else {
          var first = document.querySelector('[data-gallery-open]');
          if(first) first.click();
        }
      }
      return;
    }
    if(dx < 0) goTo(current + 1); /* Swipe left → next */
    else goTo(current - 1); /* Swipe right → prev */
  }, {passive: true});

  /* Dot click */
  dotsContainer.addEventListener('click', function(e){
    var dot = e.target.closest('[data-swipe-dot]');
    if(!dot) return;
    goTo(parseInt(dot.dataset.swipeDot));
  });

  /* Restore hero when variant changes */
  document.addEventListener('variant:changed', function(){
    restoreHero();
    origHeroSrc = heroImg.src;
    origHeroSrcset = heroImg.srcset || '';
    if(picture){
      origSources = [];
      picture.querySelectorAll('source').forEach(function(s){
        origSources.push({ el: s, srcset: s.getAttribute('srcset') });
      });
    }
  });
})();

/* ── Tablet gallery: thumbnail tap opens gallery modal + scroll arrows ── */
(function(){
  var gallery = document.querySelector('[data-cinematic="mobile-gallery"]');
  if(!gallery) return;
  gallery.addEventListener('click', function(e){
    var thumb = e.target.closest('.cpdp__mobile-gallery-thumb');
    if(!thumb) return;
    e.preventDefault();
    var mediaId = thumb.dataset.mediaId;
    var trigger = document.querySelector('[data-gallery-open][data-media-id="' + mediaId + '"]');
    if(trigger) trigger.click();
  });

  /* Scroll arrows inside gallery — sticky positioning keeps them visible */
  var arrowSvgLeft = '<svg viewBox="0 0 14 14" role="presentation" focusable="false"><path d="M9 3.2 5.2 7 9 10.8" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"></path></svg>';
  var arrowSvgRight = '<svg viewBox="0 0 14 14" role="presentation" focusable="false"><path d="M5 3.2 8.8 7 5 10.8" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"></path></svg>';

  var btnLeft = document.createElement('button');
  btnLeft.type = 'button';
  btnLeft.className = 'cpdp__gallery-scroll-nav cpdp__gallery-scroll-nav--left';
  btnLeft.setAttribute('aria-label', 'Scroll thumbnails left');
  btnLeft.innerHTML = arrowSvgLeft;

  var btnRight = document.createElement('button');
  btnRight.type = 'button';
  btnRight.className = 'cpdp__gallery-scroll-nav cpdp__gallery-scroll-nav--right';
  btnRight.setAttribute('aria-label', 'Scroll thumbnails right');
  btnRight.innerHTML = arrowSvgRight;

  gallery.appendChild(btnLeft);
  gallery.appendChild(btnRight);

  function updateArrows(){
    var sl = gallery.scrollLeft;
    var maxScroll = gallery.scrollWidth - gallery.clientWidth;
    btnLeft.hidden = sl <= 2;
    btnRight.hidden = maxScroll <= 2 || sl >= maxScroll - 2;
  }

  var scrollStep = 200;
  btnLeft.addEventListener('click', function(){ gallery.scrollBy({left: -scrollStep, behavior: 'smooth'}); });
  btnRight.addEventListener('click', function(){ gallery.scrollBy({left: scrollStep, behavior: 'smooth'}); });
  gallery.addEventListener('scroll', updateArrows, {passive: true});
  window.addEventListener('resize', updateArrows);
  updateArrows();
})();

/* ═══════════════════════════════════════════════════════════
   CINEMATIC FOOTER NEWSLETTER — hidden iframe submit + validation

   We originally tried a fetch-based AJAX submit, but Shopify's
   /contact endpoint returns 4xx on a number of common cases
   (duplicate email, session/token mismatches on anonymous AJAX
   submits) which broke the UX.

   Instead we use the classic "hidden iframe" pattern:
   - Client-side email validation runs first (format check).
   - If invalid → preventDefault + show error, stay on page.
   - If valid → set form.target to a hidden iframe, let the
     native form submit run. Shopify processes the submit as it
     always has (CSRF/session/form_type/customer flow all work),
     the response HTML loads inside the hidden iframe, and the
     main page stays exactly where it is.
   - We listen for the iframe's load event to show the success
     message and clear the input.

   This is the same technique used by hundreds of "subscribe
   without page reload" implementations. It's reliable because
   it uses Shopify's native form pipeline.
   ═══════════════════════════════════════════════════════════ */
(function initCinematicNewsletter(){
  function bindForms(){
    var forms = document.querySelectorAll('.cpdp-footer__newsletter-form');
    if (!forms.length) return;

    var emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    forms.forEach(function(form, formIndex){
      if (form.dataset.newsletterBound === 'true') return;
      form.dataset.newsletterBound = 'true';

      /* Ensure the form has the right action for Shopify's customer flow */
      if (!form.getAttribute('action') || form.getAttribute('action').indexOf('/contact') === -1) {
        form.setAttribute('action', '/contact#contact_form');
      }
      form.setAttribute('method', 'post');
      form.setAttribute('accept-charset', 'UTF-8');

      /* Create a hidden iframe that will receive the form response */
      var iframeName = 'cpdp-newsletter-target-' + Date.now() + '-' + formIndex;
      var iframe = document.createElement('iframe');
      iframe.name = iframeName;
      iframe.setAttribute('aria-hidden', 'true');
      iframe.setAttribute('tabindex', '-1');
      iframe.style.cssText = 'position:absolute;left:-9999px;top:-9999px;width:1px;height:1px;border:0;opacity:0;pointer-events:none;';
      document.body.appendChild(iframe);
      form.target = iframeName;

      /* Inline message element — sits below the form in flow */
      var wrap = form.closest('.cpdp-footer__newsletter') || form.parentNode;
      var msg = wrap.querySelector('.cpdp-footer__newsletter-msg');
      if (!msg) {
        msg = document.createElement('span');
        msg.className = 'cpdp-footer__newsletter-msg';
        msg.setAttribute('role', 'status');
        msg.setAttribute('aria-live', 'polite');
        form.insertAdjacentElement('afterend', msg);
      }

      function setMsg(text, type){
        msg.textContent = text;
        msg.className = 'cpdp-footer__newsletter-msg'
          + (type ? ' cpdp-footer__newsletter-msg--' + type : '');
      }

      var input = form.querySelector('input[type="email"]');
      var btn = form.querySelector('button[type="submit"]');

      /* Clear message as soon as the user starts editing again */
      if (input) {
        input.addEventListener('input', function(){
          setMsg('', '');
          input.setCustomValidity('');
        });
      }

      /* Browser behavior varies on whether empty-iframe creation fires a
         load event. So we ignore initial loads entirely and only react when
         we're actively waiting for a submit response. */
      var awaitingResponse = false;
      var pendingTimeout = null;

      function completeSubmit(){
        if (!awaitingResponse) return;
        awaitingResponse = false;
        if (pendingTimeout) { clearTimeout(pendingTimeout); pendingTimeout = null; }
        /* Shopify processed the submit. We can't reliably read the iframe
           contents (cross-frame read may be blocked, and the response may
           be a redirect). Client-side validation already filtered invalid
           formats, so we show success. */
        setMsg('Thank you! You\u2019re on the list.', 'success');
        if (input) input.value = '';
        if (btn) btn.disabled = false;
      }

      iframe.addEventListener('load', function(){
        /* Only treat as completion if we were actually awaiting a response.
           This harmlessly ignores any initial empty-iframe load events. */
        if (awaitingResponse) completeSubmit();
      });

      form.addEventListener('submit', function(e){
        var email = input ? (input.value || '').trim() : '';

        /* Invalid format → block submit + show inline error */
        if (!email) {
          e.preventDefault();
          e.stopPropagation();
          setMsg('Please enter your email address.', 'error');
          if (input) input.focus();
          return false;
        }
        if (!emailRegex.test(email)) {
          e.preventDefault();
          e.stopPropagation();
          setMsg('Please enter a valid email address.', 'error');
          if (input) input.focus();
          return false;
        }

        /* Valid: let Shopify handle the native submit (into the hidden
           iframe). Show loading state, mark that we're waiting. */
        setMsg('Subscribing…', 'loading');
        if (btn) btn.disabled = true;
        awaitingResponse = true;
        /* Safety net: if the iframe load event never fires (cross-origin
           redirect, 204 no-content, etc.) force completion after 3s so
           the user isn't stuck staring at "Subscribing…" forever. */
        if (pendingTimeout) clearTimeout(pendingTimeout);
        pendingTimeout = setTimeout(completeSubmit, 3000);
        /* Do NOT call preventDefault here — let the browser submit the
           form natively to the iframe target. */
      }, true /* useCapture: ensure we run before any other listener */);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindForms);
  } else {
    bindForms();
  }
  /* Safety net: re-run after a short delay in case the footer is injected
     late (portal / dynamic render) */
  setTimeout(bindForms, 500);
  setTimeout(bindForms, 2000);
})();


/* Judge.me Q&A Toast — inline below the reviews widget */
(function(){
  var qnaToastShown = false;

  var s = document.createElement('style');
  s.textContent = 'body.cpdp-page .jdgm-notification{display:none!important}';
  document.head.appendChild(s);

  document.addEventListener('click', function(e) {
    var btn = e.target;
    if (!btn) return;
    if (btn.classList && btn.classList.contains('jdgm-submit-question')) {
      setTimeout(showQnaToast, 1500);
      return;
    }
    if (btn.closest && btn.closest('.jdgm-submit-question')) {
      setTimeout(showQnaToast, 1500);
    }
  }, true);

  function showQnaToast() {
    if (qnaToastShown) return;
    qnaToastShown = true;
    setTimeout(function() { qnaToastShown = false; }, 10000);

    var old = document.querySelector('.cpdp-qna-toast');
    if (old && old.parentNode) old.parentNode.removeChild(old);

    var toast = document.createElement('div');
    toast.className = 'cpdp-qna-toast';
    toast.innerHTML = '<svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="10" cy="10" r="8"/><path d="M7 10l2 2 4-4"/></svg><span>Your question has been submitted. You will receive a notification when it is answered.</span>';
    toast.style.cssText = 'display:flex;align-items:center;gap:10px;margin:16px auto;max-width:700px;padding:14px 20px;background:rgba(143,214,168,0.1);border:1px solid rgba(143,214,168,0.3);border-radius:10px;color:#8FD6A8;font-size:13px;font-weight:500;line-height:1.5;font-family:Montserrat,Helvetica Neue,Arial,sans-serif;transition:opacity 0.4s ease;';

    /* Insert right below the reviews/Q&A widget */
    var widget = document.getElementById('cpdp-reviews')
      || document.getElementById('judgeme_product_reviews');
    if (widget) {
      widget.parentNode.insertBefore(toast, widget.nextSibling);
      toast.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } else {
      document.body.appendChild(toast);
    }

    setTimeout(function() {
      toast.style.opacity = '0';
      setTimeout(function() { if (toast.parentNode) toast.parentNode.removeChild(toast); }, 400);
    }, 10000);
  }
})();

/* ═══════════════════════════════════════════════════════════
   FAQ "ASK YOUR QUESTION" FORM — iframe submit + inline toast
   Same pattern as newsletter: hidden iframe target so page
   doesn't reload, inline success/error message below button.
   ═══════════════════════════════════════════════════════════ */
(function(){
  var forms = document.querySelectorAll('.cpdp-faq-ask__form');
  if (!forms.length) return;

  forms.forEach(function(form, i) {
    if (form.dataset.faqBound === '1') return;
    form.dataset.faqBound = '1';

    /* Create hidden iframe target */
    var iframeName = 'cpdp-faq-ask-target-' + Date.now() + '-' + i;
    var iframe = document.createElement('iframe');
    iframe.name = iframeName;
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'position:absolute;left:-9999px;top:-9999px;width:1px;height:1px;border:0;opacity:0;pointer-events:none;';
    document.body.appendChild(iframe);
    form.target = iframeName;

    var msg = form.querySelector('.cpdp-faq-ask__msg');
    var btn = form.querySelector('.cpdp-faq-ask__submit');
    var awaitingResponse = false;

    function showMsg(text, type) {
      if (!msg) return;
      msg.textContent = text;
      msg.style.cssText = 'display:block;margin-top:12px;padding:12px 16px;border-radius:8px;font-size:13px;font-weight:500;font-family:Montserrat,Helvetica Neue,Arial,sans-serif;text-align:center;'
        + (type === 'success'
          ? 'background:rgba(143,214,168,0.1);border:1px solid rgba(143,214,168,0.3);color:#8FD6A8;'
          : type === 'error'
          ? 'background:rgba(232,154,142,0.1);border:1px solid rgba(232,154,142,0.3);color:#E89A8E;'
          : 'color:rgba(241,232,222,0.5);background:transparent;border:none;');
    }

    iframe.addEventListener('load', function() {
      if (!awaitingResponse) return;
      awaitingResponse = false;
      showMsg('Thank you! Your message has been sent. We will get back to you soon.', 'success');
      if (btn) btn.disabled = false;
      /* Clear form fields */
      form.querySelectorAll('input[type="text"], input[type="email"], textarea').forEach(function(f) {
        f.value = '';
      });
      /* Auto-hide message after 10 seconds */
      setTimeout(function() { if (msg) msg.style.opacity = '0'; }, 10000);
    });

    form.addEventListener('submit', function(e) {
      var email = form.querySelector('input[type="email"]');
      var body = form.querySelector('textarea');

      if (email && !email.value.trim()) {
        e.preventDefault();
        showMsg('Please enter your email address.', 'error');
        return;
      }
      if (body && !body.value.trim()) {
        e.preventDefault();
        showMsg('Please enter your question.', 'error');
        return;
      }

      /* Valid — let native submit go to iframe */
      showMsg('Sending...', 'loading');
      if (btn) btn.disabled = true;
      awaitingResponse = true;

      /* Safety timeout — if iframe load doesn't fire in 5s, show success anyway */
      setTimeout(function() {
        if (awaitingResponse) {
          awaitingResponse = false;
          showMsg('Thank you! Your message has been sent. We will get back to you soon.', 'success');
          if (btn) btn.disabled = false;
          form.querySelectorAll('input[type="text"], input[type="email"], textarea').forEach(function(f) { f.value = ''; });
        }
      }, 5000);
    });
  });
})();
